import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  ABACPolicy,
  ABACPolicyEntity,
  abacPolicySelector,
  adjustToTimeZone,
  useGetABACPolicyQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  ABACPolicyForm,
  ABACPolicyFormDefaultValues,
  ABACPolicyFormValues
} from "../../components/form/ABACPolicyForm/ABACPolicyForm";
import {useABACPolicyMutation} from "../../hooks/useABACPolicyMutation";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: ABACPolicyFormDefaultValues
}

export const ABACPolicyFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <ABACPolicyFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const toFormDefaultsFromABACPolicy = (abacPolicy: ABACPolicyEntity | undefined): ABACPolicyFormDefaultValues => {
  return {
    name: abacPolicy?.name,
    description: abacPolicy?.description,
    expression: abacPolicy?.expression,
  };
};

export const ABACPolicyFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectABACPolicy = useMemo(() => {
    return abacPolicySelector()
  }, [])

  const {currABACPolicy, isLoading} = useGetABACPolicyQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currABACPolicy: selectABACPolicy(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: ABACPolicyFormDefaultValues = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromABACPolicy(currABACPolicy);
  }, [currABACPolicy, formDefaults]);

  const isEdit = useMemo(() => {
    return currABACPolicy && !copy
  }, [currABACPolicy, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.abacpolicy.form.title.edit", "Edit ABAC Policy", {ns: "common"});
    } else {
      return t("page.entity.abacpolicy.form.title.new", "New ABAC Policy", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createABACPolicy,
    updateABACPolicy,
    isCreating,
    isUpdating,
  } = useABACPolicyMutation()

  const onABACPolicySubmit = useCallback((formValues: ABACPolicyFormValues) => {
    const patch: Partial<ABACPolicy> = {
      name: formValues.name,
      description: formValues.description,
      expression: formValues.expression,
    }

    if (isEdit) {
      const abacPolicy: Partial<ABACPolicy> = {
        ...currABACPolicy.asTypeValue(),
        ...patch,
        lastModifiedDttm: adjustToTimeZone().getTime(),
      };
      updateABACPolicy(abacPolicy, () => navigate(-1))
    } else {
      const abacPolicy: Partial<ABACPolicy> = {
        ...patch,
      };
      createABACPolicy(abacPolicy, (payload) => navigate(`/abac-policies/profile?id=${payload.id}`, {replace: true}))
    }
  }, [currABACPolicy, isEdit, createABACPolicy, updateABACPolicy, navigate]);

  return (
    <ProtectedPage
      permissions={[isEdit ? "abacpolicy_edit" : "abacpolicy_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <ABACPolicyForm
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onABACPolicySubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

